import { Schema } from "effect"
import { Actor, ContentMeta, EntityRef, Evidence, NodeRef, Principal, RecordMeta } from "../common"
import {
  AgentId,
  AreaId,
  ArtifactId,
  CommentId,
  IntegrationId,
  Markdown,
  MemoryItemId,
  Millis,
  ProjectId,
  ProposalId,
  RunId,
  Sha256,
  Slug,
  TaskId,
  UserId,
} from "../ids"
import { ArtifactKind } from "./task"

// #region memory
/** Scope decides WHETHER an item is loaded. Narrower wins a conflict, except `locked` items from a wider scope. */
export const MemoryScope = Schema.TaggedUnion({
  user: { userId: UserId }, // personal: never shared, loaded only for agents acting on behalf of this user
  workspace: {},
  project: { projectId: ProjectId },
  area: { projectId: ProjectId, areaId: AreaId },
  agent: { agentId: AgentId }, // the notebook
  task: { projectId: ProjectId, taskId: TaskId }, // archived when the task closes
})
export type MemoryScope = typeof MemoryScope.Type

export const MemoryType = Schema.Literals([
  "rule",
  "preference",
  "fact",
  "decision",
  "learning",
  "playbook",
  "journal",
])

/** Type decides HOW an item is loaded and who may change it. Type-specific fields live here. */
export const MemoryContent = Schema.TaggedUnion({
  rule: {}, // always loaded for its scope; humans write, agents propose
  preference: {}, // always, for agents acting for its user; only that user writes
  fact: { summary: Schema.String, evidence: Schema.Array(Evidence) }, // summary always, body on demand
  decision: { decidedAt: Millis, links: Schema.Array(NodeRef), alternatives: Schema.Array(Schema.String) }, // on trigger
  learning: {
    evidence: Schema.NonEmptyArray(Evidence),
    confidence: Schema.Literals(["low", "medium", "high"]),
    revisitAt: Millis,
  }, // on trigger; agents propose, the lead curates
  playbook: { skillName: Slug }, // on demand, rendered as a skill
  journal: { runId: Schema.NullOr(RunId) }, // never loaded; recall/search only; written by the system
})

export const MemoryTriggers = Schema.Struct({
  tags: Schema.Array(Slug),
  paths: Schema.Array(Schema.String),
  integrations: Schema.Array(IntegrationId),
})

/** What a proposal carries and what a direct human edit writes. */
export const MemoryDraft = Schema.Struct({
  scope: MemoryScope,
  title: Schema.String,
  body: Markdown,
  content: MemoryContent,
  triggers: MemoryTriggers,
  /** Wider-scope rule nothing narrower may override (workspace legal rules). Humans only. */
  locked: Schema.Boolean,
})

/**
 * Stored in the scope owner's DO: user → User DO, workspace → Workspace DO, project/area/task → Project DO,
 * agent → Agent DO. Version history = events. Load statistics are a projection over run manifests, not a field.
 */
export const MemoryItem = Schema.Struct({
  ...RecordMeta, // user scope forces visibility "private"
  ...ContentMeta,
  ...MemoryDraft.fields,
  id: MemoryItemId,
  revision: Schema.Int,
  status: Schema.Literals(["accepted", "archived", "revoked"]), // "proposed" lives in the ledger, not here
  tainted: Schema.Boolean, // copied from the source run; tainted items are always rendered as quoted reference material
  reviewedBy: Schema.NullOr(Actor),
  proposalId: Schema.NullOr(ProposalId),
  sourceRunId: Schema.NullOr(RunId),
})
export type MemoryItem = typeof MemoryItem.Type
// #endregion memory

// #region artifact
/** Blobs in R2 (content-addressed by sha256); external things (PRs) are links. Graph-addressable via EntityRef. */
export const Artifact = Schema.Struct({
  ...RecordMeta,
  ...ContentMeta,
  id: ArtifactId,
  taskId: Schema.NullOr(TaskId),
  runId: Schema.NullOr(RunId),
  kind: ArtifactKind,
  title: Schema.String,
  uri: Schema.String, // "r2://artifacts/<sha256>" or "https://github.com/…/pull/12"
  contentType: Schema.NullOr(Schema.String),
  sizeBytes: Schema.NullOr(Schema.Int),
  sha256: Schema.NullOr(Sha256),
  preview: Schema.NullOr(Schema.String), // short text for cards
  interfaceKey: Schema.NullOr(Slug), // the area interface it fulfils ("keyword-list")
  tainted: Schema.Boolean, // follows the producing run; consuming it via `informs` taints the consumer
  supersedes: Schema.NullOr(ArtifactId),
  state: Schema.Literals(["draft", "final", "published", "rejected"]),
})
// #endregion artifact

// #region comment
/**
 * Threads on any entity. `ask`/`answer` comments are the agent bus: an ask goes exactly one level up
 * (worker → its task's assignee, role agent → lead), so information flow is visible on the node.
 * Questions to people are `question` proposals, not comments.
 */
export const Comment = Schema.Struct({
  ...RecordMeta,
  ...ContentMeta,
  id: CommentId,
  target: EntityRef,
  threadId: CommentId, // = id for a thread root
  kind: Schema.Literals(["note", "ask", "answer", "system"]),
  body: Markdown,
  mentions: Schema.Array(Principal), // parsed server-side at write time
  ask: Schema.NullOr(
    Schema.Struct({ to: AgentId, state: Schema.Literals(["open", "answered", "escalated"]) }),
  ),
  answers: Schema.NullOr(CommentId), // answer → the ask it answers
  /** Which mention woke whom, and whether that wake was untrusted. Explains "why did it wake?". */
  wakes: Schema.Array(Schema.Struct({ agentId: AgentId, untrusted: Schema.Boolean })),
  tainted: Schema.Boolean,
  editedAt: Schema.NullOr(Millis), // humans only; agents post corrections
  resolvedAt: Schema.NullOr(Millis),
})
// #endregion comment
